/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import Point from '@arcgis/core/geometry/Point';
import debounce from 'lodash.debounce';
import sortby from 'lodash.sortby';
import uniqby from 'lodash.uniqby';
import dynamic from 'next/dynamic';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {AssetType} from '@ion/core/src/types/entities';
import {mToKm, mToMi} from '@ion/core/src/units/conversions';
import {getUserUnit, isImperial} from '@ion/core/src/units/helpers';
import RootManager from '@ion/dls/src/client/RootManager';
import {FLEET_ASSET_LAYER_ID, FLEET_SELECTED_ASSET_LAYER_ID} from '@ion/dls/src/maps/constants';
import {getDistanceByCoordinates} from '@ion/dls/src/maps/helpers/DistanceUtils';
import {getLayerByTitle, unmountComponent} from '@ion/dls/src/maps/helpers/MapViewUtils';
import {FindNearbyProps, MapContainerProps} from '@ion/dls/src/maps/types/types';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {
  setBoundaryFilter,
  setFindNearbyActive,
  setFindNearbyDistance,
  setFindNearbyFromItem,
  setFindNearbyPanelActive,
  setFindNearbyRefresh,
  setFindNearbyResults,
  setFindNearbySelectedAsset
} from '@/store/actions/tracking';
import {
  findNearbyV2SelectedAssetSelector,
  findNearbyV2Selector,
  trackingFiltersSelector
} from '@/store/selectors/tracking-selectors';
import {getFindNearbyLocation} from '../../../helpers';

interface FindNearbyContainerProps extends MapContainerProps {
  isFleetAssetLayerRendered?: boolean;
  onSetMapId?: (mapId: string) => void;
};
    
const DynamicFindNearbyContainer = dynamic(() => import('@ion/dls/src/maps/findNearby/FindNearbyContainer'), {
  ssr : false
});

const getAssetDefinition = id => `assetId <> ${id}`;
  
/**
* @FindNearbyContainer
*
* Defines a container to wrap the Find Nearby Panel
*
* ```typescript
*
*   import FindNearbyContainer from '@ion/dls/src/maps/toolbar/FindNearbyContainer';
*
*   <FindNearbyContainer map={map} view={view} />
*
* ```
*/
const FindNearbyContainer = ({map, view, isFleetAssetLayerRendered, onSetMapId}: FindNearbyContainerProps) => {
  const dispatch                   = useDispatch();
  const fleetAssetLayerRef         = useRef(null);
  const fleetAssetLayerViewRef     = useRef(null);
  const fleetSelectedAssetLayerRef = useRef(null);
  const omitToQueryLayer           = useRef(false);
  const isPanelActiveRef           = useRef(null);
  const isActiveRef                = useRef(null);
  const isMounted                  = useHasMounted(null);
  const findNearby                 = useSelector(findNearbyV2Selector);
  const filters                    = useSelector(trackingFiltersSelector);
  const selectedAsset              = useSelector(findNearbyV2SelectedAssetSelector);
  const selectedAssetRef           = useRef(selectedAsset);
    
  const isUomMiles                 = isImperial(getUserUnit());
  const distanceConversion         = isUomMiles ? mToMi : mToKm;
  const units                      = isUomMiles ? 'miles' : 'kilometers';
  const {
    refresh,
    results,
    fromItem,
    isActive,
    distance,
    isPanelActive
  } = findNearby || {};
    
  // these two highlight handlers are used for selection and hovering over features
  let highlightSelect;
    
  const {name, location} = fromItem || {} as any;

  useEffect(() => {
    selectedAssetRef.current = selectedAsset;
  }, [selectedAsset]);
    
  const setFindNearbyActions = useCallback(location => {
    dispatch(setFindNearbyFromItem(getFindNearbyLocation(location)));
    dispatch(setFindNearbyPanelActive(true));
  }, []);
    
  const onFindNearbyPanelActive = useCallback((isActive: boolean) => {
    if (!isActive) {
      dispatch(setFindNearbySelectedAsset(null));
    }
    dispatch(setFindNearbyPanelActive(isActive));
  }, []);
    
  const onFindNearbyActive = useCallback((isActive: boolean) => {
    if (!isActive) {
      dispatch(setFindNearbySelectedAsset(null));
    }
    dispatch(setFindNearbyActive(isActive));
  }, []);
    
  const getGeometry = useCallback((longitude, latitude, distance) => {
    const point    = new Point({longitude, latitude});
    const geometry = geometryEngine.geodesicBuffer(point, distance, units);
    return geometry;
  }, [units]);
  
  const filterFleetAssetLayer = useCallback((removeFilter: boolean = false) => {
    const {boundary} = filters || {};
    const {location} = findNearby?.fromItem || {};
    const {filter}   = fleetAssetLayerViewRef.current || {};
    if (location && findNearby?.isPanelActive && !removeFilter) {
      const geometry = getGeometry(location.longitude, location.latitude, findNearby.distance);
      const featureFilter = {
        geometry,
        spatialRelationship : 'contains'
      };
      if (filter) {
        featureFilter.where = filter?.where;
      }
      fleetAssetLayerViewRef.current.filter = featureFilter;
      queryFleetAssetLayer();
      dispatch(setBoundaryFilter([]));
    } else {
      if (!boundary?.length) {
        fleetAssetLayerViewRef.current.filter = {
          where : filter?.where
        };
      }
    }
    fleetAssetLayerRef.current.refresh();
  }, [findNearby, filters]);
    
  const queryFleetAssetLayer = useCallback(async() => {
    let results          = [];
    const {id, location} = findNearby?.fromItem || {};
    const {filter}       = fleetAssetLayerViewRef.current || {};
    if (location) {
      const {longitude, latitude} = location;
      const geometry  = getGeometry(longitude, latitude, findNearby.distance);
      const query     = fleetAssetLayerRef.current.createQuery();
      query.geometry  = geometry;
      query.outFields = ['OBJECTID', 'assetId', 'assetName', 'assetType', 'longitude', 'latitude'];
      query.spatialRelationship = 'contains';
      if (filter?.where) {
        query.where = filter?.where;
      }
      if (id) {
        const assetDefinition = getAssetDefinition(id);
        query.where += (filter?.where || query.where) ? ` AND ${assetDefinition}` : assetDefinition;
      }
      const selectedAsset = selectedAssetRef.current
        ? await fleetSelectedAssetLayerRef.current.queryFeatures(`assetId = ${selectedAssetRef.current.assetId}`)
          .then(({features}) => features)
        : [];
      fleetAssetLayerRef.current.queryFeatures(query).then(({features}) => {
        const assets = [];
        results      = [...features, ...selectedAsset];
        if (results.length) {
          const assetCoordinates = {latitude, longitude};
          results?.forEach(({attributes}) => {
            const {OBJECTID, assetId, assetName, assetType, longitude, latitude} = attributes;
            const distanceValue = getDistanceByCoordinates(
              assetCoordinates,
              {longitude, latitude}
            );
            const distance = Math.round((distanceConversion(distanceValue)) * 10) / 10;
            assets.push({
              OBJECTID,
              assetId,
              assetName,
              distance,
              assetType : AssetType[assetType] || 'Vehicle'
            });
          });
          const sortedAssets = sortby(uniqby(assets, 'assetId'), ['distance']);
          dispatch(setFindNearbyResults(sortedAssets));
        } else {
          dispatch(setFindNearbyResults([]));
        }
      });
    }
  }, [findNearby, units, selectedAssetRef.current]);
    
  const onFindNearbySearch = useCallback(() => {
    if (!fleetAssetLayerRef.current || !fleetAssetLayerViewRef.current) {
      fleetAssetLayerRef.current         = getLayerByTitle(map?.layers as any, FLEET_ASSET_LAYER_ID);
      fleetSelectedAssetLayerRef.current = getLayerByTitle(map?.layers as any, FLEET_SELECTED_ASSET_LAYER_ID);
      fleetAssetLayerRef.current?.when(async() => {
        fleetAssetLayerViewRef.current = await view.whenLayerView(fleetAssetLayerRef.current);
        filterFleetAssetLayer();
      });
    } else {
      filterFleetAssetLayer();
    }
    onFindNearbyActive(true);
    omitToQueryLayer.current = true;
  }, [findNearby, view, map]);
    
  const onFindNearbyDistanceChange = useCallback(debounce((distance: number) => {
    dispatch(setFindNearbyDistance(distance));
  }, 100), []);

  useEffect(() => {
    isPanelActiveRef.current = isPanelActive;
  }, [isPanelActive]);

  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);
    
  useEffect(function() {
    if (!isPanelActive && fleetAssetLayerRef.current && fleetAssetLayerViewRef.current) {
      filterFleetAssetLayer();
    }
    isPanelActive && view?.closePopup();
  }, [isPanelActive]);
    
  useEffect(function() {
    if (refresh && isActive && fleetAssetLayerRef.current) {
      queryFleetAssetLayer();
    }
    dispatch(setFindNearbyRefresh(false));
  }, [refresh, isActive]);
    
  useEffect(function() {
    if (!isActive && isPanelActive && fleetAssetLayerRef.current &&
      fleetAssetLayerViewRef.current) {
      filterFleetAssetLayer(true);
    }
  }, [isActive, isPanelActive]);

  useEffect(function() {
    if (isActive && location && !omitToQueryLayer.current) {
      if (fleetAssetLayerRef.current && fleetAssetLayerViewRef.current) {
        filterFleetAssetLayer();
      } else {
        onFindNearbySearch();
      }
    }
    omitToQueryLayer.current = false;
  }, [isActive, location]);
    
  const highlightFeature = useCallback(debounce(value => {
    if (selectedAssetRef.current?.assetId !== value?.assetId) {
      const query = fleetAssetLayerRef.current?.createQuery();
      query.where = `assetId = ${value.assetId}`;
      fleetAssetLayerRef.current?.queryFeatures(query).then(({features}) => {
        const {attributes} = features?.[0] || {};
        if (attributes) {
          dispatch(setFindNearbySelectedAsset({...attributes, isCvsAsset: true}));
        }
      });
    } else {
      dispatch(setFindNearbySelectedAsset(null));
    }
  }, 300), [highlightSelect, selectedAsset]);
  
  // in the current theme the widget's own React root is unmounted, so the
  // range it drew on the map is cleared with it - only taking its container
  // off the page left the range behind after a switch from the legacy theme.
  // The theme is read as the widget goes, since it may have just switched ...
  const viewRef = useRef(view);
  viewRef.current = view;
  const unMount = useCallback((id) => {
    if (isCurrentTheme()) {
      unmountComponent({id, view: viewRef.current});
    } else {
      RootManager.removeContainer(RootManager.getContainer(id));
    }
  }, []);
    
  useEffect(() => {
    return () => {
      unMount('map-findNearby-container');
      dispatch(setFindNearbySelectedAsset(null));
    };
  }, []);

  useEffect(() => {
    if (isPanelActiveRef.current && isActiveRef.current && isFleetAssetLayerRendered) {
      onFindNearbySearch();
      omitToQueryLayer.current = false;
    }
  }, [isFleetAssetLayerRendered]);
    
  const findNearbyProps: FindNearbyProps = useMemo(() => ({
    highlightFeature,
    onSetMapId,
    onFindNearbySearch,
    onFindNearbyActive,
    setFindNearbyActions,
    findNearbyName                : name,
    findNearbyResults             : results,
    findNearbyDistance            : distance,
    findNearbyLocation            : location,
    isFindNearbyPanelActive       : isPanelActive,
    findNearbySelectedAsset       : selectedAsset,
    isFindNearbySearchPanelActive : isPanelActive && !isActive,
    setFindNearbyPanelActive      : onFindNearbyPanelActive,
    setFindNearbyDistance         : onFindNearbyDistanceChange
  }), [
    name,
    results,
    distance,
    isActive,
    location,
    onSetMapId,
    isPanelActive,
    selectedAsset,
    highlightFeature,
    onFindNearbySearch,
    onFindNearbyActive,
    setFindNearbyActions,
    onFindNearbyPanelActive,
    onFindNearbyDistanceChange
  ]);
    
  if (isMounted) {
    return (
      <DynamicFindNearbyContainer map={map}
        view={view}
        {...findNearbyProps} />
    );
  }
};
    
export default FindNearbyContainer;
